import { useEffect, useState, useCallback, useRef } from "react";
import { RefreshCw, AlertTriangle } from "lucide-react";

// Hash van de main-bundle die NU draait (uit de <script>-tag in index.html).
const runningMain = () => {
  try {
    const s = document.querySelector('script[src*="/static/js/main."]');
    const m = s && s.src ? s.src.match(/main\.([a-zA-Z0-9]+)\.js/) : null;
    return m ? m[1] : "";
  } catch (e) {
    return "";
  }
};

// Verplichte versie-update: zodra er een nieuwere gedeployde bundel bestaat dan
// die nu draait, wordt de hele app geblokkeerd met een volledig-scherm overlay.
// De gebruiker MOET vernieuwen; er is geen manier om verder te werken op de oude
// versie. Er loopt ook een auto-teller die na 10s vanzelf vernieuwt, zodat een
// achtergelaten/inactief scherm zichzelf altijd bijwerkt.
export function VersionChecker() {
  const [stale, setStale] = useState(false);
  const [busy, setBusy] = useState(false);
  const [count, setCount] = useState(10);
  const done = useRef(false);

  const check = useCallback(async () => {
    try {
      const res = await fetch(`/asset-manifest.json?ts=${Date.now()}`, { cache: "no-store" });
      if (!res.ok) return;
      const j = await res.json();
      const dep = String((j.files && j.files["main.js"]) || "").match(/main\.([a-zA-Z0-9]+)\.js/);
      const deployedHash = dep ? dep[1] : "";
      const running = runningMain();
      if (deployedHash && running && deployedHash !== running) setStale(true);
    } catch (e) {
      /* offline of geen manifest (dev): negeren */
    }
  }, []);

  useEffect(() => {
    check();
    const id = setInterval(check, 30000);
    const onVis = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
  }, [check]);

  const refresh = useCallback(async () => {
    if (done.current) return;
    done.current = true;
    setBusy(true);
    try {
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
      if (window.caches) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch (e) {
      /* ignore */
    }
    window.location.reload();
  }, []);

  // Auto-teller: dwingt vernieuwing af zodra de overlay verschijnt.
  useEffect(() => {
    if (!stale) return;
    setCount(10);
    const id = setInterval(() => {
      setCount((c) => {
        if (c <= 1) { clearInterval(id); refresh(); return 0; }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [stale, refresh]);

  if (!stale) return null;
  return (
    <div data-testid="version-update-overlay" className="fixed inset-0 z-[100000] flex items-center justify-center bg-slate-900/80 backdrop-blur-sm p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-7 text-center shadow-2xl">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-amber-100">
          <AlertTriangle className="h-7 w-7 text-amber-600" />
        </div>
        <h2 className="text-xl font-bold text-slate-900">Nieuwe versie vereist</h2>
        <p className="mt-2 text-sm text-slate-600">
          Er is een nieuwere versie van de app beschikbaar. Om met de juiste (audit-proof) gegevens te werken moet je nu vernieuwen. De oude versie kan niet meer gebruikt worden.
        </p>
        <button
          data-testid="version-refresh-btn"
          disabled={busy}
          onClick={refresh}
          className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#1C64B0] px-4 py-3 text-sm font-bold text-white transition-colors hover:bg-blue-800 disabled:opacity-60"
        >
          <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} />
          {busy ? "Vernieuwen…" : `Nu vernieuwen${count > 0 ? ` (automatisch over ${count}s)` : ""}`}
        </button>
      </div>
    </div>
  );
}
